import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, Input, Signal, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Journal, JournalSetup } from '@app/_interfaces/journal.interface';
import { CacheService } from '@app/_services/cache.service';
import { JournalStore } from '@app/_stores';
import { getLocalDateString as getDateString } from '@app/_services/date';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
// JournalFormComponent loaded dynamically to break circular dependency
import { ParseMinutesPipe } from '@app/_pipes/parse-minutes.pipe';
import { ParseTimeLocalPipe } from '@app/_pipes/parse-time-local.pipe';
import { InputCheckmarkAdvancedComponent } from '@app/components/shared/inputs/input-checkmark-advanced/input-checkmark-advanced.component';
import { EntitySetup } from '@app/_interfaces/feature.interface';
import { ParseRulePipe } from '@app/_pipes/parse-rule.pipe';
import { ParseTextPipe } from '@app/_pipes/parse-text.pipe';
import { MapService } from '@app/_services/map.service';
import { DependencyService } from '@app/_services/dependency.service';
import { MeUserAvatarComponent } from '../../../common/account/me-user-avatar/me-user-avatar.component';
import { SettingsService } from '@app/_services/settings.service';
import { Attachment } from '@app/_interfaces/generic.interface';
import { LastUpdatedByService } from '@app/_services/last-updated-by.service';
import { CollaboraterRole, EntityStatus } from '@app/_types/generic.type';
import { getRoleByUid, getCollaboratorCount } from '@app/_utils/members-model.utils';
import { ModalService } from '@app/_services/modal.service';

@Component({
    selector: 'app-journal-block',
    imports: [
        CommonModule,
        SvgComponent,
        ParseTimeLocalPipe,
        ParseMinutesPipe,
        InputCheckmarkAdvancedComponent,
        ParseRulePipe,
        ParseTextPipe,
        MeUserAvatarComponent
    ],
    templateUrl: './journal-block.component.html',
    styleUrl: './journal-block.component.scss'
})

export class JournalBlockComponent implements OnInit {

  readonly journalStore = inject(JournalStore);
  readonly lastUpdatedByService = inject(LastUpdatedByService);
  // Use signal inputs for properties used in computed signals
  entity = input.required<EntitySetup>();
  dateString = input<string>(getDateString());
  @Input() show: string[] = [];
  @Input() blockClass: string = '';
  @Input() descriptionType: string = 'none';
  @Input() isLabel: boolean = true;
  @Input() clickable: boolean = true;
  @Input() hideStatus: boolean = false;

  journal: Signal<Journal | null> = computed(() => {
    return this.journalStore.journalMap().setups[this.entity().id || '']?.[this.dateString()] || null;
  });
  setup: Signal<JournalSetup> = computed(() => {
    return this.journalStore.idToSetup()[this.entity().id || ''];
  });

  hashtags: Signal<string[]> = computed(() => {
    const setupTags = this.setup().tags;
    const journalTags = this.journal() ? this.journal()!.tags : [];
    const tags = [...setupTags, ...journalTags];
    const validHashtags = this.ss.getValidHashtags(tags);
    const uniqueHashtags = [...new Set(validHashtags)];
    return uniqueHashtags;
  });

  attachments: Signal<Attachment[]> = computed(() => {
    return Object.values(this.journal()?.attachments || {}).filter(atch => atch.deletedAt === null);
  });

  deleted: Signal<boolean> = computed(() => {
    const deletedAt = this.setup().deletedAt;
    if (!deletedAt) return false;
    return true;
  });

  role: Signal<CollaboraterRole> = computed(() => {
    if (this.setup().uid === this.cc.user.uid) {
      return 'owner';
    } else if (this.setup().members) {
      return getRoleByUid(this.setup().members, this.cc.user.uid) || 'viewer';
    } else {
      return 'viewer';
    }
  });
  collabCount: Signal<number> = computed(() => {
    return getCollaboratorCount(this.setup().members);
  });

  /**
   * Computed signal for journal completion status using live data
   * This ensures the checkbox reflects the current journal data, not stale entity status
   */
  isCompleted: Signal<boolean> = computed(() => {
    const journal = this.journal();
    if (!journal) return false;

    // Journal is complete if it has completedAt and matches the current date
    return journal.completedAt !== null && journal.dueAt?.dateString === this.dateString();
  });

  /**
   * Computed signal for journal draft status using live data
   * Draft = has content but not marked complete
   */
  isDraft: Signal<boolean> = computed(() => {
    const journal = this.journal();
    if (!journal) return false;

    // If already completed, not a draft
    if (this.isCompleted()) return false;

    // Draft if has any content: description, emotion, or attachments
    const hasDescription = journal.description && journal.description.trim() !== '';
    const hasEmotion = journal.emotion !== null;
    const hasAttachments = journal.attachments && Object.keys(journal.attachments).length > 0;

    return hasDescription || hasEmotion || hasAttachments;
  });

  /**
   * Computed signal for date validation (overdue check)
   * A journal date is invalid if the date has completely passed (before today)
   */
  isDateInvalid: Signal<boolean> = computed(() => {
    // If completed or in draft, not invalid
    if (this.isCompleted() || this.isDraft()) return false;

    const dateStr = this.dateString();
    if (!dateStr) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const journalDate = new Date(dateStr);
    journalDate.setHours(0, 0, 0, 0);

    // Invalid if the date is before today (not including today)
    return journalDate.getTime() < today.getTime();
  });

  /**
   * Computed signal for time validation (overdue check)
   * A journal time is invalid if the specific time has passed
   */
  isTimeInvalid: Signal<boolean> = computed(() => {
    // If completed or in draft, not invalid
    if (this.isCompleted() || this.isDraft()) return false;

    const setup = this.setup();
    const dateStr = this.dateString();

    // If no date string or time not set, can't be invalid
    if (!dateStr || !setup.isStartTimeSet) return false;

    const [hours, minutes] = setup.startAt.timeString.split(':').map(Number);
    const dueDateTime = new Date(dateStr);
    dueDateTime.setHours(hours, minutes, 0, 0);

    const now = new Date();
    return now.getTime() > dueDateTime.getTime();
  });

  /**
   * Computed signal for missed status (combines date and time checks)
   * Missed = overdue on date or time with no completion or draft
   */
  isMissed: Signal<boolean> = computed(() => {
    return this.isDateInvalid() || this.isTimeInvalid();
  });

  constructor(
    public cc: CacheService,
    public dialog: MatDialog,
    public mapService: MapService,
    public ds: DependencyService,
    public ss: SettingsService,
    private modalService: ModalService
  ) {

  }

  ngOnInit() {

  }

  async openJournal(setupId: string) {
    // Dynamic import to break circular dependency:
    // journal-block -> journal-form -> journal-setup-form -> journal-entries -> journal-block
    const { JournalFormComponent } = await import('../journal-form/journal-form.component');
    const confirmDialog = this.modalService.openLevel1(
      JournalFormComponent,
      {
        mode: this.journal() ? 'edit' : 'new',
        value: this.journal(),
        setupId: setupId,
        dateString: this.dateString()
      },
      { disableClose: true, autoFocus: false }
    );
    // return confirmDialog.afterClosed();
  }
}
